import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../utils/api';
import { handleApiError } from '../../utils/errorHandler';
import ThemeToggle from '../ThemeToggle';
import DashboardTab from './DashboardTab';
import ChildrenTab from './ChildrenTab';
import SchoolClassesTab from './SchoolClassesTab';
import AssignClassModal from './AssignClassModal';

const ChildManagement = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [error, setError] = useState(null);
  
  // Classes management state (shared between tabs)
  const [classes, setClasses] = useState([]);
  
  // Class assignment state
  const [assignClassModalOpen, setAssignClassModalOpen] = useState(false);
  const [selectedChildForClass, setSelectedChildForClass] = useState(null);
  const [childrenRefreshKey, setChildrenRefreshKey] = useState(0);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  
  const { logout } = useAuth();

  useEffect(() => {
    loadClasses();
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [activeTab]);

  const loadClasses = async () => {
    try {
      setError(null);
      const response = await api.get('/subjects?include_inactive=false');
      setClasses(response.data);
    } catch (err) {
      console.error('Error loading classes:', err);
      setError(handleApiError(err));
    } finally {
    }
  };

  const handleAssignClassClick = (child) => {
    setSelectedChildForClass(child);
    setAssignClassModalOpen(true);
  };

  const handleClassAssigned = async () => {
    // Refresh classes + children cards after assign/unassign.
    await loadClasses();
    setChildrenRefreshKey((prev) => prev + 1);
  };

  const openPasswordModal = () => {
    setPasswordError('');
    setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    setShowPasswordModal(true);
  };

  const closePasswordModal = () => {
    setShowPasswordModal(false);
    setPasswordLoading(false);
    setPasswordError('');
    setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
  };

  const handlePasswordChange = (event) => {
    const { name, value } = event.target;
    setPasswordForm((prev) => ({ ...prev, [name]: value }));
  };

  const submitPasswordChange = async (event) => {
    event.preventDefault();
    setPasswordError('');

    if (!passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
      setPasswordError('Please fill in all password fields.');
      return;
    }
    if (passwordForm.newPassword.length < 6) {
      setPasswordError('Password must be at least 6 characters.');
      return;
    }
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError('New Password and Confirm New Password must match');
      return;
    }

    try {
      setPasswordLoading(true);
      await api.put('/auth/change-password', {
        current_password: passwordForm.currentPassword,
        new_password: passwordForm.newPassword,
      });
      closePasswordModal();
      window.alert('Password updated successfully.');
    } catch (err) {
      setPasswordError(handleApiError(err));
    } finally {
      setPasswordLoading(false);
    }
  };

  return (
    <div className="dashboard-container parent-dashboard-view">
      <div className="parent-navbar">
        <div className="parent-navbar-content">
          <h1>
            Manage Children <span className="mobile-title-break">&amp; Classes</span>
          </h1>
          <div className="parent-navbar-tabs">
            <button
              className={`parent-navbar-tab ${activeTab === 'dashboard' ? 'active' : ''}`}
              onClick={() => setActiveTab('dashboard')}
            >
              Dashboard
            </button>
            <button
              className={`parent-navbar-tab ${activeTab === 'children' ? 'active' : ''}`}
              onClick={() => setActiveTab('children')}
            >
              Children
            </button>
            <button
              className={`parent-navbar-tab ${activeTab === 'classes' ? 'active' : ''}`}
              onClick={() => setActiveTab('classes')}
            >
              School Classes
            </button>
          </div>
          <div className="parent-navbar-actions">
            <button className="btn-secondary" onClick={openPasswordModal}>
              Change Password
            </button>
            <ThemeToggle />
            <button className="btn-logout" onClick={logout}>
              Logout
            </button>
          </div>
          <button
            className="parent-navbar-hamburger"
            onClick={() => setMobileMenuOpen(true)}
            aria-label="Open menu"
          >
            ☰
          </button>
        </div>
      </div>

      {/* Mobile Menu Backdrop */}
      <div
        className={`parent-mobile-menu-backdrop ${mobileMenuOpen ? 'open' : ''}`}
        onClick={() => setMobileMenuOpen(false)}
      />

      {/* Mobile Menu */}
      <div className={`parent-mobile-menu ${mobileMenuOpen ? 'open' : ''}`}>
        <div className="parent-mobile-menu-header">
          <h2>Menu</h2>
          <button
            className="parent-mobile-menu-close"
            onClick={() => setMobileMenuOpen(false)}
            aria-label="Close menu"
          >
            ×
          </button>
        </div>
        <div className="parent-mobile-menu-tabs">
          <button
            className={`parent-mobile-menu-tab ${activeTab === 'dashboard' ? 'active' : ''}`}
            onClick={() => {
              setActiveTab('dashboard');
              setMobileMenuOpen(false);
            }}
          >
            Dashboard
          </button>
          <button
            className={`parent-mobile-menu-tab ${activeTab === 'children' ? 'active' : ''}`}
            onClick={() => {
              setActiveTab('children');
              setMobileMenuOpen(false);
            }}
          >
            Children
          </button>
          <button
            className={`parent-mobile-menu-tab ${activeTab === 'classes' ? 'active' : ''}`}
            onClick={() => {
              setActiveTab('classes');
              setMobileMenuOpen(false);
            }}
          >
            School Classes
          </button>
        </div>
        <div className="parent-mobile-menu-actions">
          <button
            className="btn-secondary"
            onClick={() => {
              openPasswordModal();
              setMobileMenuOpen(false);
            }}
          >
            Change Password
          </button>
          <ThemeToggle />
          <button
            className="btn-logout"
            onClick={() => {
              logout();
              setMobileMenuOpen(false);
            }}
          >
            Logout
          </button>
        </div>
      </div>

      {error && (
        <div className="error-message" style={{ marginBottom: '1rem', padding: '1rem', borderRadius: '8px' }}>
          {error}
        </div>
      )}

      {/* Dashboard Tab */}
      {activeTab === 'dashboard' && (
        <DashboardTab onSwitchToChildren={() => setActiveTab('children')} />
      )}

      {/* Children Tab */}
      {activeTab === 'children' && (
        <ChildrenTab 
          classes={classes} 
          refreshKey={childrenRefreshKey}
          onAssignClassClick={handleAssignClassClick}
        />
      )}

      {/* Classes Tab */}
      {activeTab === 'classes' && (
        <SchoolClassesTab 
          classes={classes}
          onClassChange={loadClasses}
        />
      )}

      {/* Assign Class Modal */}
      {assignClassModalOpen && selectedChildForClass && (
        <AssignClassModal
          child={selectedChildForClass}
          classes={classes}
          onClose={async () => {
            await handleClassAssigned();
            setAssignClassModalOpen(false);
            setSelectedChildForClass(null);
          }}
          onClassAssigned={handleClassAssigned}
        />
      )}

      {showPasswordModal && (
        <div className="modal-overlay" onClick={closePasswordModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h2>Change Password</h2>
              <button className="modal-close-btn" onClick={closePasswordModal} aria-label="Close modal">
                ×
              </button>
            </div>
            <div className="modal-body">
              <form onSubmit={submitPasswordChange} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {passwordError && (
                  <div className="error-message" style={{ marginBottom: 0 }}>
                    {passwordError}
                  </div>
                )}
                <div className="form-group">
                  <label style={{ marginBottom: '0.25rem', fontSize: '0.9rem', fontWeight: 600 }}>Current Password</label>
                  <input
                    type="password"
                    name="currentPassword"
                    value={passwordForm.currentPassword}
                    onChange={handlePasswordChange}
                    placeholder="Enter current password"
                    minLength={1}
                    style={{ padding: '0.75rem', fontSize: '0.9rem', width: '100%' }}
                  />
                </div>
                <div className="form-group">
                  <label style={{ marginBottom: '0.25rem', fontSize: '0.9rem', fontWeight: 600 }}>New Password</label>
                  <input
                    type="password"
                    name="newPassword"
                    value={passwordForm.newPassword}
                    onChange={handlePasswordChange}
                    placeholder="Enter new password"
                    minLength={6}
                    style={{ padding: '0.75rem', fontSize: '0.9rem', width: '100%' }}
                  />
                </div>
                <div className="form-group">
                  <label style={{ marginBottom: '0.25rem', fontSize: '0.9rem', fontWeight: 600 }}>Confirm New Password</label>
                  <input
                    type="password"
                    name="confirmPassword"
                    value={passwordForm.confirmPassword}
                    onChange={handlePasswordChange}
                    placeholder="Repeat new password"
                    minLength={6}
                    style={{ padding: '0.75rem', fontSize: '0.9rem', width: '100%' }}
                  />
                  {passwordForm.confirmPassword && passwordForm.newPassword !== passwordForm.confirmPassword && (
                    <div style={{ marginTop: '0.25rem', color: '#dc2626', fontSize: '0.8rem' }}>
                      Passwords do not match
                    </div>
                  )}
                  {passwordForm.newPassword &&
                    passwordForm.confirmPassword &&
                    passwordForm.newPassword === passwordForm.confirmPassword && (
                      <div style={{ marginTop: '0.25rem', color: '#16a34a', fontSize: '0.8rem', fontWeight: 600 }}>
                        ✓ Passwords match
                      </div>
                    )}
                </div>
                <div style={{ display: 'flex', gap: '0.75rem' }}>
                  <button type="button" className="btn-secondary" onClick={closePasswordModal} style={{ flex: 1 }}>
                    Cancel
                  </button>
                  <button type="submit" className="btn" disabled={passwordLoading} style={{ flex: 1 }}>
                    {passwordLoading ? 'Saving...' : 'Update Password'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChildManagement;
